// AddToDoRequestModal.js
import React, { useState } from 'react'
import { Grid } from '@material-ui/core'
import { ModalDialog } from '../../../../components/common/Dialogs'
import { SpacingContainer } from '../../../../components/common/Containers'
import { TextField } from '../../../../components/common/Inputs'
import { Button } from '../../../../components/common/Buttons'
import Typography from '../../../../components/common/Typography'
import { useTrans } from '../../../../services/i18n'
import { useAlertContext } from '../../../../contexts/AlertContext'
import connect from '../connect'

// Same validation rules you use on the backend:
// - title required
// - link OR notes required (at least one)
// - link must be http(s) URL if provided
const isHttpUrl = (str) => /^https?:\/\/.+/i.test(String(str || '').trim())

const AddToDoRequestModal = ({
  open,
  onClose,
  organizationId,
  createToDoRequest,
  getToDoRequests
}) => {
  const { trans } = useTrans()
  const { successAlert, errorAlert } = useAlertContext()
  const [isSending, setIsSending] = useState(false)
  const [form, setForm] = useState({ title: '', link: '', notes: '' })
  const [errors, setErrors] = useState({})

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
    setErrors({ ...errors, [e.target.name]: '' })
  }

  const validate = () => {
    const next = {}
    if (!String(form.title || '').trim()) next.title = trans('title-required')
    const hasLink = !!String(form.link || '').trim()
    const hasNotes = !!String(form.notes || '').trim()
    if (!hasLink && !hasNotes) next.linkNotes = trans('link-or-notes-required')
    if (hasLink && !isHttpUrl(form.link)) next.link = trans('link-must-be-http')
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const resetAndClose = () => {
    setForm({ title: '', link: '', notes: '' })
    setErrors({})
    onClose?.()
  }

  const handleSave = async () => {
    if (!organizationId) {
      errorAlert(trans('missing-organization-id') || 'Missing organizationId')
      return
    }
    if (!validate()) return
    setIsSending(true)
    try {
      // Build payload exactly as backend expects
      const payload = {
        id: organizationId,                 // API expects org id as "id"
        title: form.title.trim()
      }
      const link = (form.link || '').trim()
      const notes = (form.notes || '').trim()
      if (link) payload.link = link
      if (notes) payload.notes = notes

      await createToDoRequest(payload)
      await getToDoRequests({ id: organizationId })
      successAlert(trans('to-do-created') || 'To-Do created')
      resetAndClose()
    } catch (e) {
      errorAlert(trans('error-saving-to-do') || 'Error saving')
    } finally {
      setIsSending(false)
    }
  }

  const handleCancel = () => {
    resetAndClose()
  }

  return (
    <ModalDialog
      open={open}
      onClose={handleCancel}
      title={trans('add-to-do-request') || 'Add To-Do Request'}
      actions={
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item>
            <Button variant="outlined" onClick={handleCancel} disabled={isSending}>
              {trans('cancel')}
            </Button>
          </Grid>
          <Grid item>
            <Button variant="contained" color="success" pending={isSending} onClick={handleSave}>
              {trans('save')}
            </Button>
          </Grid>
        </Grid>
      }
    >
      <SpacingContainer px={3} py={2}>
        {/* Subtitle under the modal title */}
        <Typography
          variant="body1"
          color="textPrimary"
          style={{ marginBottom: 32, marginTop: -20 }}
        >
          
            You can create a custom To-Do request that will appear on the organization's Dashboard
        </Typography>

        <TextField
          fullWidth
          label={trans('title') || 'Title'}
          name="title"
          value={form.title}
          onChange={handleChange}
          error={!!errors.title}
          helperText={errors.title || ' '}
          style={{ marginBottom: 12 }}
          placeholder="e.g., Update your Pipeline information"
        />
        <TextField
          fullWidth
          label='Link'
          name="link"
          value={form.link}
          onChange={handleChange}
          error={!!errors.link || !!errors.linkNotes}
          helperText={errors.link || errors.linkNotes || ' '}
          placeholder="https://example.com/page"
          style={{ marginBottom: 12 }}
        />
        <TextField
          fullWidth
          label='Notes'
          name="notes"
          value={form.notes}
          onChange={handleChange}
          multiline
          rows={3}
          error={!!errors.linkNotes}
          helperText={errors.linkNotes ? ' ' : ''}
          placeholder="Additional details or instructions..."
        />
      </SpacingContainer>
    </ModalDialog>
  )
}

export default connect(AddToDoRequestModal)
